(function () {
  'use strict';

  const templatesSelector = '#disclaimer-templates-data';
  const containerSelector = '[data-disclaimer-container]';
  const checkoutSelector = '[data-disclaimer-checkout]';
  const dynamicCheckoutSelector = '[data-disclaimer-dynamic-checkout]';
  const lineItemSelector = '[data-disclaimer-tags]';
  const productDynamicSelector = '[data-product-dynamic-checkout]';
  const productVariantsSelector = '[data-product-disclaimer-variants]';

  let templates = [];
  let checkedTags = new Set();
  let rendering = false;
  let renderQueued = false;
  let consentSaving = false;

  const isRequired = (template) =>
    template && (template.required === true || template.required === 'true' || template.required === 1 || template.required === '1');

  function loadTemplates() {
    const node = document.querySelector(templatesSelector);
    if (!node) { templates = []; return; }
    try {
      const parsed = JSON.parse(node.textContent || '[]');
      templates = Array.isArray(parsed) ? parsed : [];
      templates.sort((a, b) => Number(a.sortOrder || 0) - Number(b.sortOrder || 0));
    } catch (error) {
      console.error('Could not parse disclaimer templates', error);
      templates = [];
    }
  }

  function tagsFromValue(value) {
    if (Array.isArray(value)) return value.map(String).map((x) => x.trim()).filter(Boolean);
    return String(value || '').split(',').map((x) => x.trim()).filter(Boolean);
  }

  function activeTags() {
    const result = new Set();
    document.querySelectorAll(lineItemSelector).forEach((row) => {
      tagsFromValue(row.getAttribute('data-disclaimer-tags')).forEach((tag) => result.add(tag));
    });
    return result;
  }

  function requiredActiveTags() {
    const active = activeTags();
    return new Set(templates.filter((t) => active.has(t.tag) && isRequired(t)).map((t) => t.tag));
  }

  function allRequiredChecked() {
    return [...requiredActiveTags()].every((tag) => checkedTags.has(tag));
  }

  function renderCheckboxes() {
    if (rendering) return;
    rendering = true;
    try {
      loadTemplates();
      const active = activeTags();
      document.querySelectorAll(containerSelector).forEach((container) => {
        container.replaceChildren();
        const activeTemplates = templates.filter((t) => active.has(t.tag));
        const activeRequired = activeTemplates.filter(isRequired);

        if (activeRequired.length && allRequiredChecked()) {
          const details = document.createElement('details');
          details.className = 'disclaimer-accepted-details';
          const summary = document.createElement('summary');
          summary.textContent = 'View disclaimer';
          details.addEventListener('toggle', () => {
            summary.textContent = details.open ? 'Hide disclaimer' : 'View disclaimer';
          });
          details.appendChild(summary);

          const content = document.createElement('div');
          content.className = 'disclaimer-accepted-content';
          activeTemplates.forEach((template) => {
            const label = document.createElement('label');
            label.className = 'disclaimer-checkbox';
            const input = document.createElement('input');
            input.type = 'checkbox';
            input.dataset.disclaimerTag = template.tag;
            input.checked = checkedTags.has(template.tag);
            input.addEventListener('change', onCheckboxChange);
            label.append(input, document.createTextNode(' ' + template.label));
            content.appendChild(label);
          });
          details.appendChild(content);
          container.appendChild(details);

          const status = document.createElement('div');
          status.className = 'disclaimer-consent-success';
          status.setAttribute('role', 'status');
          status.textContent = 'Thank you for accepting the disclaimer. You can now proceed to checkout.';
          container.appendChild(status);
        } else {
          activeTemplates.forEach((template) => {
            const label = document.createElement('label');
            label.className = 'disclaimer-checkbox';
            const input = document.createElement('input');
            input.type = 'checkbox';
            input.dataset.disclaimerTag = template.tag;
            input.checked = checkedTags.has(template.tag);
            input.addEventListener('change', onCheckboxChange);
            label.append(input, document.createTextNode(' ' + template.label));
            container.appendChild(label);
          });
        }
      });
      updateGateState();
    } finally {
      rendering = false;
    }
  }

  function queueRender() {
    if (renderQueued) return;
    renderQueued = true;
    requestAnimationFrame(() => { renderQueued = false; renderCheckboxes(); });
  }

  function updateGateState() {
    const allowed = allRequiredChecked() && !consentSaving;
    document.querySelectorAll(checkoutSelector).forEach((button) => {
      button.disabled = !allowed;
      button.setAttribute('aria-disabled', String(!allowed));
      button.classList.toggle('disclaimer-checkout-disabled', !allowed);
    });
    document.querySelectorAll(dynamicCheckoutSelector).forEach((wrapper) => {
      wrapper.hidden = !allowed;
      wrapper.style.pointerEvents = allowed ? '' : 'none';
    });
    updateProductDynamicCheckout();
  }

  async function onCheckboxChange(event) {
    const input = event.target;
    const tag = input.dataset.disclaimerTag;
    const template = templates.find((t) => t.tag === tag);

    if (!input.checked) {
      checkedTags.delete(tag);
      updateGateState();
      return;
    }

    consentSaving = true;
    updateGateState();

    const timestamp = new Date().toISOString();
    const attributes = {
      ['disclaimer_consent_' + tag]: template ? template.label : tag,
      ['disclaimer_consent_' + tag + '_timestamp']: timestamp
    };

    try {
      const response = await fetch('/cart/update.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ attributes })
      });
      if (!response.ok) throw new Error('Cart update failed with status ' + response.status);
      checkedTags.add(tag);
    } catch (error) {
      input.checked = false;
      checkedTags.delete(tag);
      console.error('Failed to save disclaimer consent', error);
    } finally {
      consentSaving = false;
      document.querySelectorAll('[data-disclaimer-tag="' + CSS.escape(tag) + '"]').forEach((box) => { box.checked = checkedTags.has(tag); });
      renderCheckboxes();
      updateGateState();
    }
  }

  function blockCheckout(event) {
    if (allRequiredChecked()) return;
    event.preventDefault();
    event.stopImmediatePropagation();
    const first = document.querySelector(containerSelector + ' input[type="checkbox"]:not(:checked)');
    if (first) first.focus();
    return false;
  }

  function productVariantData() {
    const node = document.querySelector(productVariantsSelector);
    if (!node) return [];
    try { return JSON.parse(node.textContent || '[]') || []; }
    catch (error) { console.error('Could not parse product disclaimer variants', error); return []; }
  }

  function updateProductDynamicCheckout() {
    const variants = productVariantData();
    const inputs = [...document.querySelectorAll('.product-variant-id')];
    const selectedId = inputs.find((input) => input.value)?.value || document.querySelector('[data-product-disclaimer-variants]')?.dataset?.selectedVariantId;
    const variant = variants.find((v) => String(v.id) === String(selectedId));
    const tags = tagsFromValue(variant && variant.tags);
    const requires = tags.some((tag) => templates.some((t) => t.tag === tag && isRequired(t)));
    const blocked = requires;

    document.querySelectorAll(productDynamicSelector).forEach((wrapper) => {
      wrapper.hidden = blocked;
      wrapper.style.display = blocked ? 'none' : '';
      wrapper.setAttribute('aria-hidden', String(blocked));
    });

    // Reveal the product-page express checkout only after the first
    // complete evaluation has finished. This prevents an initial flash
    // on disclaimer products while still allowing ordinary products
    // to display express checkout.
    document.documentElement.classList.add('disclaimer-consent-ready');
  }

  function init() {
    loadTemplates();
    renderCheckboxes();

    document.addEventListener('submit', (event) => {
      const form = event.target;
      if (form && (form.id === 'cart' || form.id === 'CartDrawer-Form')) blockCheckout(event);
    }, true);

    document.addEventListener('click', (event) => {
      const button = event.target.closest && event.target.closest(checkoutSelector);
      if (button) blockCheckout(event);
    }, true);

    document.addEventListener('click', (event) => {
      const closeLink = event.target.closest && event.target.closest('[data-disclaimer-close-drawer]');
      if (!closeLink) return;
      event.preventDefault();
      const drawer = closeLink.closest('cart-drawer');
      const closeButton = drawer && drawer.querySelector('.drawer__close');
      if (closeButton) closeButton.click();
    });

    document.addEventListener('change', (event) => {
      if (event.target.closest && event.target.closest('variant-selects, variant-radios, .product-form')) {
        setTimeout(updateProductDynamicCheckout, 0);
      }
    });
    setTimeout(updateProductDynamicCheckout, 0);
    setTimeout(updateProductDynamicCheckout, 100);
    setInterval(updateProductDynamicCheckout, 500);

    new MutationObserver((mutations) => {
      if (rendering) return;
      if (mutations.some((m) => [...m.addedNodes].some((n) => n.nodeType === 1 && (n.matches?.(lineItemSelector) || n.querySelector?.(lineItemSelector))))) queueRender();
      updateProductDynamicCheckout();
    }).observe(document.body, { childList: true, subtree: true });
  }

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', init);
  else init();
})();
